import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteAdminOrder, emailAdminOrder, getAdminOrder, retryAdminOrder } from '../../../services/adminApi';
import useConfirm from '../shell/useConfirm';
import { Drawer, Tag } from '../catalog/ui';
import { formatEuros } from '../catalog/productForm';
import '../catalog/catalog.css';
import './orders.css';

// FICHE D'UNE COMMANDE (panneau latéral) : tout ce qu'il faut pour comprendre une commande et, si elle est en échec, la corriger sans
// aller dans la base. Les actions disent ce qui va se passer AVANT, dans la page. L'annulation et le remboursement restent un geste
// manuel dans Stripe (lien direct) : rien n'est remboursé ni annulé automatiquement.

export const TON_GROUPE = { echec: 'bad', paiement: 'mute', preparation: 'mute', fabrication: 'warn', expediee: '', livree: 'ok', annulee: 'mute' };

export const argent = (cents, devise = 'EUR') => {
  if (devise === 'EUR') return formatEuros(cents);
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: devise }).format((Number(cents) || 0) / 100); } catch (_e) { return formatEuros(cents); }
};

export default function OrderDrawer({ id, onClose, onChanged, onDeleted }) {
  const { t, i18n } = useTranslation('admin');
  const [fiche, setFiche] = useState(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [enCours, setEnCours] = useState('');
  const [typeEmail, setTypeEmail] = useState('');
  const [confirmer, confirmation] = useConfirm();

  const charger = useCallback(async () => {
    try {
      const r = await getAdminOrder(id);
      setFiche(r.order);
      setErreur('');
      setTypeEmail((precedent) => (r.order.actions.emails.includes(precedent) ? precedent : (r.order.actions.emails[0] || '')));
    } catch (err) {
      setErreur(err?.message || t('orders.drawer.loadFailed'));
    }
  }, [id, t]);
  useEffect(() => { setFiche(null); charger(); }, [charger]);

  const date = (valeur) => (valeur ? new Date(valeur).toLocaleString(i18n.language) : null);

  const relancer = async () => {
    setMessage('');
    setErreur('');
    const fournisseur = t(`orders.providers.${fiche.provider.id}`, { defaultValue: fiche.provider.id });
    if (!(await confirmer({
      title: t('orders.drawer.retryTitle'),
      message: t('orders.drawer.retryBody', { provider: fournisseur }),
      confirmLabel: t('orders.drawer.retryYes'),
      danger: false
    }))) return;
    setEnCours('retry');
    try {
      await retryAdminOrder(fiche.id);
      setMessage(t('orders.drawer.retryDone'));
      await charger();
      onChanged();
    } catch (err) {
      setErreur(err?.message || t('orders.drawer.retryFailed'));
    } finally {
      setEnCours('');
    }
  };

  const renvoyer = async () => {
    setMessage('');
    setErreur('');
    if (!(await confirmer({
      title: t('orders.drawer.emailTitle'),
      message: t('orders.drawer.emailBody', { kind: t(`orders.emailKinds.${typeEmail}`), email: fiche.customer.email || t('orders.drawer.noEmail') }),
      confirmLabel: t('orders.drawer.emailYes'),
      danger: false
    }))) return;
    setEnCours('email');
    try {
      const r = await emailAdminOrder(fiche.id, typeEmail);
      setMessage(r.sent ? t('orders.drawer.emailSent') : t('orders.drawer.emailNotSent', { reason: r.skipped || '—' }));
      await charger();
    } catch (err) {
      setErreur(err?.message || t('orders.drawer.emailFailed'));
    } finally {
      setEnCours('');
    }
  };

  const supprimer = async () => {
    setMessage('');
    setErreur('');
    if (!(await confirmer({
      title: t('orders.drawer.deleteTitle'),
      message: t('orders.drawer.deleteBody', { number: fiche.number || fiche.id.slice(0, 8) }),
      confirmLabel: t('orders.drawer.deleteYes')
    }))) return;
    setEnCours('delete');
    try {
      await deleteAdminOrder(fiche.id);
      onDeleted();
    } catch (err) {
      setErreur(err?.message || t('orders.drawer.deleteFailed'));
      setEnCours('');
    }
  };

  const titre = fiche ? t('orders.drawer.title', { number: fiche.number || fiche.id.slice(0, 8) }) : t('orders.drawer.titleLoading');
  const prestataire = fiche && fiche.provider ? t(`orders.providers.${fiche.provider.id}`, { defaultValue: fiche.provider.id }) : null;

  return (
    <Drawer title={titre} onRequestClose={onClose}>
      {!fiche && !erreur && <p className="adm-state" role="status">{t('catalog.common.loading')}</p>}
      {erreur && <p className="adm-error" role="alert">{erreur}</p>}
      {message && <p className="adm-note adm-flash" role="status">{message}</p>}
      {confirmation}

      {fiche && (
        <>
          <div className="adm-switchrow">
            <Tag tone={TON_GROUPE[fiche.group]}>{t(`orders.groups.${fiche.group}`)}</Tag>
            {fiche.mode === 'test' && <Tag tone="warn">{t('orders.modes.testTag')}</Tag>}
            <span className="adm-note">{t(`orders.types.${fiche.type}`)} · {date(fiche.createdAt)}</span>
          </div>

          {fiche.provider && fiche.provider.error && fiche.group === 'echec' && (
            <div className="adm-alert is-bad" role="alert">
              <div>
                <b>{t('orders.drawer.failureTitle', { provider: prestataire })}</b>
                <p className="adm-quote">{fiche.provider.error}</p>
                <p>{t('orders.drawer.failureHelp')}</p>
              </div>
            </div>
          )}
          {fiche.group === 'echec' && !(fiche.provider && fiche.provider.error) && (
            <div className="adm-alert is-bad" role="alert"><div><b>{t('orders.drawer.failedNoMessage')}</b></div></div>
          )}

          <dl className="adm-kv">
            <dt>{t('orders.drawer.customer')}</dt>
            <dd>{fiche.customer.name || '—'}<br /><span className="adm-note">{fiche.customer.email || '—'}{fiche.customer.country ? ` · ${fiche.customer.country}` : ''}</span></dd>
            <dt>{t('orders.drawer.book')}</dt>
            <dd>{fiche.book.title || '—'}<br /><span className="adm-note">{[fiche.book.format && t(`orders.formats.${fiche.book.format}`, { defaultValue: fiche.book.format }), fiche.book.pages && t('orders.drawer.pages', { count: fiche.book.pages }), fiche.quantity > 1 && `× ${fiche.quantity}`].filter(Boolean).join(' · ')}</span></dd>
            <dt>{t('orders.drawer.provider')}</dt>
            <dd>
              {prestataire || t('orders.providers.aucun')}
              {fiche.provider && fiche.provider.orderId && <span className="adm-note"><br />{t('orders.drawer.providerOrder', { id: fiche.provider.orderId })}{fiche.provider.status ? ` · ${fiche.provider.status}` : ''}</span>}
            </dd>
            <dt>{t('orders.drawer.totalPaid')}</dt>
            <dd><b>{argent(fiche.money.totalCents, fiche.money.currency)}</b>{fiche.money.shippingCents > 0 && <span className="adm-note"> {t('orders.drawer.includingShipping', { amount: argent(fiche.money.shippingCents, fiche.money.currency) })}</span>}</dd>
            {fiche.money.promo && (<><dt>{t('orders.drawer.promo')}</dt><dd>{fiche.money.promo.code} (−{fiche.money.promo.percentOff} %, −{argent(fiche.money.promo.discountCents, fiche.money.currency)})</dd></>)}
            <dt>{t('orders.drawer.cost')}</dt>
            <dd>{fiche.money.estimatedCostCents === null ? '—' : argent(fiche.money.estimatedCostCents, 'EUR')}<span className="adm-note"> {t('orders.drawer.estimated')}</span></dd>
            <dt>{t('orders.drawer.margin')}</dt>
            <dd>{fiche.money.estimatedMarginCents === null ? '—' : <b>{argent(fiche.money.estimatedMarginCents, 'EUR')}</b>}<span className="adm-note"> {t('orders.drawer.marginNote')}</span></dd>
          </dl>

          <section className="adm-dsec">
            <h3>{t('orders.drawer.timeline')}</h3>
            <ul className="adm-tl">
              {fiche.timeline.map((etape) => (
                <li key={etape.key} className={etape.state === 'ok' ? '' : etape.state}>
                  <i aria-hidden="true" />
                  <div>
                    <b>{t(`orders.steps.${etape.key === 'sent' && etape.state === 'err' ? 'sentFailed' : etape.key}`, { provider: prestataire })}</b>
                    <small>{etape.state === 'todo' ? t('orders.drawer.waiting') : (date(etape.at) || '')}{etape.detail ? ` · ${etape.detail}` : ''}</small>
                  </div>
                </li>
              ))}
            </ul>
            {fiche.tracking && (
              <p className="adm-note">
                {t('orders.drawer.tracking')} : {fiche.tracking.carrier ? `${fiche.tracking.carrier} ` : ''}
                {fiche.tracking.url ? <a href={fiche.tracking.url} target="_blank" rel="noopener noreferrer">{fiche.tracking.code}</a> : fiche.tracking.code}
              </p>
            )}
          </section>

          <section className="adm-dsec">
            <h3>{t('orders.drawer.actions')}</h3>
            <div className="adm-actions">
              <button type="button" className="btn btn-primary btn-sm" onClick={relancer} disabled={!fiche.actions.retry || Boolean(enCours)}>
                {enCours === 'retry' ? t('orders.drawer.retrying') : t('orders.drawer.retry')}
              </button>
            </div>
            <p className="adm-note">{fiche.actions.retry ? t('orders.drawer.retryHelp') : t('orders.drawer.retryNone')}</p>

            {fiche.actions.emails.length > 0 && (
              <div className="adm-actions">
                <label className="adm-sr" htmlFor="adm-email-kind">{t('orders.drawer.emailChoose')}</label>
                <select id="adm-email-kind" className="adm-input adm-select" value={typeEmail} onChange={(e) => setTypeEmail(e.target.value)}>
                  {fiche.actions.emails.map((k) => <option key={k} value={k}>{t(`orders.emailKinds.${k}`)}</option>)}
                </select>
                <button type="button" className="btn btn-outline btn-sm" onClick={renvoyer} disabled={!typeEmail || Boolean(enCours)}>
                  {enCours === 'email' ? t('orders.drawer.emailing') : t('orders.drawer.email')}
                </button>
              </div>
            )}

            <div className="adm-actions">
              {fiche.stripeUrl
                ? <a className="btn btn-outline btn-sm" href={fiche.stripeUrl} target="_blank" rel="noopener noreferrer">{t('orders.drawer.openStripe')}</a>
                : <span className="adm-note">{t('orders.drawer.noStripe')}</span>}
            </div>
            <p className="adm-note">{t('orders.drawer.cancelHelp')}</p>
            <div className="adm-actions">
              <button type="button" className="btn btn-outline btn-sm adm-btn-danger" onClick={supprimer} disabled={!fiche.actions.delete || Boolean(enCours)}>
                {enCours === 'delete' ? t('orders.drawer.deleting') : t('orders.drawer.delete')}
              </button>
            </div>
            <p className="adm-note">{fiche.actions.delete ? t('orders.drawer.deleteHelp') : t(fiche.mode === 'test' ? 'orders.drawer.deleteNoneProduction' : 'orders.drawer.deleteNoneReal')}</p>
          </section>

          <section className="adm-dsec">
            <details>
              <summary><h3>{t('orders.drawer.journal')}</h3></summary>
              {fiche.events.length === 0 ? <p className="adm-note">{t('orders.drawer.journalEmpty')}</p> : (
                <ul className="adm-plain">
                  {fiche.events.map((e) => (
                    <li key={`${e.at}-${e.message}`}><small>{date(e.at)}{e.level === 'error' ? ` · ${t('orders.drawer.levelError')}` : ''}</small>{e.message}</li>
                  ))}
                </ul>
              )}
            </details>
          </section>
        </>
      )}
    </Drawer>
  );
}
